import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Download } from "lucide-react";
import NetworkCanvas from "../components/NetworkCanvas";
import Marquee from "../components/Marquee";
import { MaskedLines, FadeUp, SectionTag, EASE } from "../components/motion";
import { SOLUTIONS, DELIVERY_STEPS, WHY_SGS, QUALITY_STAGES, MANIFESTO, INDUSTRIES, IMAGES } from "../data/content";
import useSEO from "../hooks/useSEO";

const HERO_METRICS = [
  { value: "2014", label: "Established" },
  { value: "08", label: "Solution Domains" },
  { value: "09", label: "Stage Delivery Lifecycle" },
  { value: "24×7", label: "On-Site & Offshore Support" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col overflow-hidden grid-bg noise-overlay" data-testid="hero-section">
      <NetworkCanvas />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FFFFFF] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full flex-1 flex flex-col justify-center pt-16 pb-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-[#FFFFFF]/90 border border-slate-200 mb-8"
              data-testid="hero-telemetry-badge"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              <span className="font-mono text-[11px] tracking-[0.18em] text-slate-600 uppercase">Systems Operational · Patna HQ</span>
            </motion.div>

            <h1 className="font-heading font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-[4.2rem]" data-testid="hero-headline">
              <MaskedLines
                lines={["Technology Solutions.", "Engineering Excellence.", { text: "Business Impact.", className: "text-blue-600" }]}
                lineClassName="text-slate-900"
              />
            </h1>

            <FadeUp delay={0.7}>
              <p className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
                SATI GLOBAL SYSTEMS delivers integrated IT, surveillance, networking, software,
                infrastructure and project-management solutions — designed around the evolving
                needs of modern enterprises.
              </p>
            </FadeUp>

            <FadeUp delay={0.85} className="mt-9 flex flex-wrap gap-4">
              <Link to="/solutions" data-testid="hero-cta-solutions"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors">
                Explore Our Solutions
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-experts"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-slate-700 font-semibold text-sm hover:border-blue-600/60 hover:text-blue-600 transition-colors">
                Talk to Our Experts <ArrowUpRight size={16} />
              </Link>
              <a href="/SGS-Company-Profile-2026.pdf" download data-testid="hero-download-profile"
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-slate-600 font-semibold text-sm hover:text-blue-600 transition-colors">
                <Download size={15} /> Company Profile
              </a>
            </FadeUp>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="hidden lg:block lg:col-span-5"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-3 border border-blue-600/20 rounded-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-blue-50/40">
                <img src={IMAGES.heroServer} alt="Enterprise server infrastructure"
                  className="w-full h-[420px] object-cover spotlight-img" data-testid="hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-blue-700/90 uppercase">Infrastructure / 24×7 Ops</span>
                  <span className="w-2 h-2 rotate-45 bg-blue-600 shadow-[0_0_14px_rgba(6,182,212,0.9)]" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
        className="relative z-10 border-t border-slate-200/80 bg-[#F1F5F9]/70 backdrop-blur-sm"
        data-testid="hero-metrics-bar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
          {HERO_METRICS.map((m, i) => (
            <div key={i} className={`py-6 lg:py-7 ${i > 0 ? "lg:border-l border-slate-200/80 lg:pl-8" : ""}`} data-testid={`hero-metric-${i}`}>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-blue-600">{m.value}</div>
              <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">{m.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function Intro() {
  const highlights = ["2014 — Established", "Technology-Driven Solutions", "End-to-End Project Execution", "On-Site & Offshore Capability"];
  return (
    <section className="py-24 sm:py-32" data-testid="intro-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <FadeUp><SectionTag>Who We Are</SectionTag></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              Technology Partner for the <span className="text-blue-600">Digital Enterprise</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Established in 2014, SATI GLOBAL SYSTEMS has grown into a multidisciplinary
              technology company spanning software development, IT infrastructure, CCTV
              surveillance, networking, chip-level maintenance, consultancy and project
              management — serving enterprise, institutional and public-sector clients.
            </p>
          </FadeUp>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {highlights.map((h, i) => (
              <FadeUp key={h} delay={0.25 + i * 0.08}>
                <div className="flex items-center gap-2.5 text-sm text-slate-700" data-testid={`intro-highlight-${i}`}>
                  <CheckCircle2 size={16} className="text-blue-600 shrink-0" /> {h}
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.55}>
            <Link to="/about" data-testid="intro-about-link"
              className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
              Our story <ArrowRight size={15} />
            </Link>
          </FadeUp>
        </div>
        <FadeUp delay={0.2} className="relative">
          <div className="absolute -inset-3 border border-slate-200 rounded-2xl" />
          <div className="relative rounded-2xl overflow-hidden border border-slate-200">
            <img src={IMAGES.teamMeeting} alt="SGS engineering team" className="w-full h-[380px] object-cover spotlight-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/70 to-transparent" />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function SolutionsBento() {
  const spans = ["lg:col-span-2", "", "", "", "", "", "", "lg:col-span-2"];
  return (
    <section className="py-24 sm:py-32 bg-[#F1F5F9] border-y border-slate-200/60" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <FadeUp><SectionTag>Core Solutions</SectionTag></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                Eight disciplines. <span className="text-blue-600">One delivery engine.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <p className="max-w-md text-sm text-slate-500 leading-relaxed">
              Every engagement draws on the full stack — hardware, networks, software and
              surveillance — integrated by a single accountable team.
            </p>
          </FadeUp>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SOLUTIONS.map((s, i) => (
            <FadeUp key={s.slug} delay={(i % 4) * 0.08} className={spans[i]}>
              <Link to={`/solutions/${s.slug}`} data-testid={`solution-card-${s.slug}`}
                className="group relative block h-full bg-panel border border-slate-200 rounded-xl p-6 hover:border-blue-600/40 hover:bg-panelhover transition-colors duration-300 overflow-hidden">
                <div className="absolute top-0 right-0 font-heading font-black text-6xl text-slate-200 leading-none p-4 select-none">{s.num}</div>
                <s.icon size={26} className="text-blue-600" strokeWidth={1.6} />
                <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-blue-600/80 group-hover:text-blue-600 transition-colors">
                  Explore <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function DeliveryLifecycle() {
  const [active, setActive] = useState(0);
  return (
    <section className="py-24 sm:py-32" data-testid="delivery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>Global Delivery Model</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
            A nine-stage lifecycle, governed end to end
          </h2>
        </FadeUp>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {DELIVERY_STEPS.map((step, i) => (
              <button
                key={step.title}
                onClick={() => setActive(i)}
                data-testid={`lifecycle-step-${i + 1}`}
                className={`text-left p-4 rounded-lg border transition-colors duration-300 ${
                  active === i
                    ? "border-blue-600/60 bg-blue-50/30"
                    : "border-slate-200 bg-panel hover:border-slate-300"
                }`}
              >
                <span className={`font-mono text-[10px] tracking-[0.2em] ${active === i ? "text-blue-600" : "text-slate-600"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={`block mt-1.5 text-sm font-semibold ${active === i ? "text-slate-900" : "text-slate-600"}`}>
                  {step.title}
                </span>
              </button>
            ))}
          </div>
          <div className="lg:col-span-5">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="h-full bg-gradient-to-br from-panel via-[#F8FAFC] to-obsidian border border-slate-200 rounded-2xl p-8"
              data-testid="lifecycle-detail-panel"
            >
              <span className="font-heading font-black text-5xl text-blue-600/20">{String(active + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-heading text-xl font-semibold text-slate-900">{DELIVERY_STEPS[active].title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{DELIVERY_STEPS[active].desc}</p>
              <div className="mt-6 h-1 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${((active + 1) / DELIVERY_STEPS.length) * 100}%` }} />
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                Stage {active + 1} of {DELIVERY_STEPS.length}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-24 sm:py-32 bg-[#F1F5F9] border-y border-slate-200/60" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>The SGS Standard</SectionTag></FadeUp>
        <div className="mt-10 divide-y divide-slate-200/70">
          {MANIFESTO.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.1}>
              <div className="py-10 grid lg:grid-cols-12 gap-6 items-baseline group" data-testid={`manifesto-chapter-${c.num}`}>
                <span className="lg:col-span-2 font-heading font-black text-5xl lg:text-6xl text-outline group-hover:text-blue-600/30 transition-colors duration-500">
                  CH.{c.num}
                </span>
                <h3 className="lg:col-span-4 font-heading text-xl sm:text-2xl font-semibold text-slate-900">{c.title}</h3>
                <p className="lg:col-span-6 text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyAndQuality() {
  return (
    <section className="py-24 sm:py-32" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <FadeUp><SectionTag>Why SGS</SectionTag></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Built for clients who cannot afford failure
              </h2>
            </FadeUp>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {WHY_SGS.map((w, i) => (
                <FadeUp key={w.title} delay={i * 0.07}>
                  <div className="bg-panel border border-slate-200 rounded-xl p-5 hover:border-blue-600/40 transition-colors h-full" data-testid={`why-card-${i}`}>
                    <w.icon size={20} className="text-blue-600" strokeWidth={1.7} />
                    <h3 className="mt-3 font-heading font-semibold text-slate-900 text-[15px]">{w.title}</h3>
                    <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">{w.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <div className="lg:pl-10 lg:border-l border-slate-200/70">
            <FadeUp><SectionTag>Quality & Governance</SectionTag></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Quality built into every stage
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                A documented Quality Management System ensures software and IT services
                conform to specified requirements — verified at every gate, not inspected at the end.
              </p>
            </FadeUp>
            <div className="mt-8 space-y-2.5">
              {QUALITY_STAGES.map((q, i) => (
                <FadeUp key={q} delay={0.15 + i * 0.06}>
                  <div className="flex items-center gap-4 p-3.5 rounded-lg border border-slate-200 bg-panel/60" data-testid={`quality-stage-${i}`}>
                    <span className="font-mono text-xs text-blue-600 w-7">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-slate-700 font-medium">{q}</span>
                    <span className="ml-auto w-1.5 h-1.5 rotate-45 bg-emerald-500" />
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IndustriesPreview() {
  return (
    <section className="py-24 sm:py-32 bg-[#F1F5F9] border-y border-slate-200/60" data-testid="industries-preview">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <FadeUp><SectionTag>Industries</SectionTag></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                Sectors we serve
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <Link to="/industries" data-testid="industries-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
              All industries <ArrowRight size={15} />
            </Link>
          </FadeUp>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <FadeUp key={ind.slug} delay={(i % 4) * 0.07}>
              <Link to="/industries" data-testid={`industry-tile-${ind.slug}`}
                className="group block bg-panel border border-slate-200 rounded-xl p-6 hover:border-blue-600/40 hover:bg-panelhover transition-colors h-full">
                <ind.icon size={24} className="text-slate-500 group-hover:text-blue-600 transition-colors" strokeWidth={1.6} />
                <h3 className="mt-4 font-heading font-semibold text-slate-700 text-[15px] group-hover:text-blue-700 transition-colors">{ind.name}</h3>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="cta-band">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <FadeUp>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
            Have a project in mind?<br /><span className="text-blue-600">Let's engineer it together.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-6 text-slate-600 max-w-xl mx-auto">
            Share your requirement — our solutions team responds to every enquiry within 1–2 business days.
          </p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-9 flex flex-wrap justify-center gap-4">
          <Link to="/contact" data-testid="cta-discuss-project"
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors">
            Discuss Your Project <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/projects" data-testid="cta-view-projects"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-slate-300 text-slate-700 font-semibold text-sm hover:border-blue-600/60 hover:text-blue-600 transition-colors">
            View Case Studies
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "SATI Global Systems (SGSNet) | CCTV, Surveillance, Networking & IT Solutions in Patna",
    "SATI Global Systems (SGSNet), Patna — CCTV camera surveillance, networking, IT infrastructure, software development, chip-level maintenance, EV-ready smart city projects, business process automation and project management. MSME registered & GeM authorized. Est. 2014."
  );
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Intro />
      <SolutionsBento />
      <DeliveryLifecycle />
      <Manifesto />
      <WhyAndQuality />
      <IndustriesPreview />
      <CtaBand />
    </main>
  );
}
